/**
 * GENERIC per-column CELL CONFIGURATION — the ONE definition every table type
 * (Normal / Editable / Drill-Down, plus the matrix / pivot / grouped shapes)
 * renders from.
 *
 * A `TableCellConfig` describes how the cells of ONE column look and behave:
 *   - `display`   → how the value is drawn (plain text, badge/pill, data bar,
 *                   progress bar, heat map, trend arrow, colour dot, link);
 *   - static look → text colour / background / bold / italic for every cell;
 *   - `valueColors[]` → a flat VALUE → COLOUR map (OEM Position: MFN navy,
 *                   Strategic OEM green…): badge fill / tint, dot and bar
 *                   accent, else the text colour; `badgeFill` picks soft / solid;
 *   - `rules[]`   → CONDITIONAL look: the first rule whose condition matches
 *                   the value (or another column's value) wins and overrides
 *                   the static look (text colour, background, bold, label…);
 *   - `highlight` → soft whole-cell emphasis;
 *   - `prefix` / `suffix` / `tooltip` → text decoration;
 *   - `options` / `optionsFromData` → the dropdown values when the column's
 *                   cells are edited through a select.
 *
 * The builder writes it ONCE per column (`TmColumnDef.cellConfig`, a
 * drill-down measure's / dimension's `cellConfig`), the capability / header
 * carries it to the published view, and the two renderers call the pure
 * helpers below — nothing table-type-specific lives here, so a new table type
 * gets every cell feature by calling `resolveCellStyle` + reading `display`.
 *
 * No Angular dependency: unit-testable, usable from both the pure model
 * builder and the components.
 */

/** How a cell value is DRAWN. */
export type TableCellDisplay =
  | 'text'      // plain value (default)
  | 'badge'     // pill with the (rule) label or the value, tinted
  | 'databar'   // value-scaled bar behind the number (column min..max)
  | 'progress'  // 0..100 progress bar
  | 'heatmap'   // background scaled from `heatFrom` to `heatTo` across the column range
  | 'trend'     // ▲ / ▼ / – arrow coloured by sign
  | 'icon'      // colour dot before the value (rule colour / accent)
  | 'link'      // anchor to `linkTemplate` ({value} / {field} tokens)
  | 'buttons';  // one or more buttons (see `buttons`) in place of the value

/** Which input a cell edits through (Normal/Editable tables map this to the
 *  column `control`; the drill-down maps it to its own editor markers). */
export type TableCellEditor =
  | 'none' | 'text' | 'number' | 'dropdown' | 'checkbox' | 'date' | 'textarea';

/** Rule operators. Numeric ones coerce "1,234" / "$12k" style text; text ones
 *  compare case-insensitively; `empty` / `notEmpty` test blanks; `any` always
 *  matches (a catch-all last rule). */
export type TableCellRuleOp =
  | '>' | '>=' | '<' | '<=' | '=' | '!=' | 'between'
  | 'contains' | 'notContains' | 'startsWith' | 'endsWith' | 'in' | 'notIn'
  | 'empty' | 'notEmpty' | 'any';

/** ONE conditional rule: when the condition holds, apply the look. */
export interface TableCellRule {
  op: TableCellRuleOp;
  /** Compared value (number or text). `between` uses `value`..`value2`;
   *  `in` / `notIn` take a comma-separated list. */
  value?: string | number;
  value2?: string | number;
  /** Test ANOTHER column's value instead of this cell's own (row-aware
   *  rules: colour Price when Margin < 0). Blank = this column. */
  field?: string;
  /** Look when matched. */
  color?: string;
  background?: string;
  bold?: boolean;
  italic?: boolean;
  /** Badge / pill text (display 'badge'); blank = the cell value. */
  label?: string;
  /** Glyph drawn before the value (any short text / emoji). */
  icon?: string;
}

/** ONE entry of a column's VALUE COLOUR map ("OEM Position": MFN → navy,
 *  Strategic OEM → green, Close partners → blue…). Values compare trimmed and
 *  case-insensitively (numbers also by numeric value). */
export interface TableCellValueColor {
  value: string;
  /** The value's colour: a badge's fill (solid) or tint (soft), the dot / bar
   *  accent, else the text colour. */
  color: string;
  /** Text colour over the badge (blank = white or dark, whichever contrasts). */
  textColor?: string;
  /** Badge text for this value (blank = the value). */
  label?: string;
}

/** How a badge is painted: 'soft' = the colour as text over a translucent
 *  wash of itself; 'solid' = the colour as the fill, contrasting text. */
export type TableCellBadgeFill = 'soft' | 'solid';

export interface TableCellConfig {
  display?: TableCellDisplay;
  /** Badge paint (display 'badge'). Absent = 'solid' when value colours are
   *  set (coloured chips per value), else 'soft' — see cellBadgeFill. */
  badgeFill?: TableCellBadgeFill;
  /** Per-value colours — a flat value → colour map applied before the rules
   *  (a matching rule still overrides). Saved as a list; a `{ value: colour }`
   *  object is accepted from hand-written config. */
  valueColors?: TableCellValueColor[];
  /** Accent colour: bar fill, progress fill, dot, heat-map `to`, badge tint
   *  when no rule colour applies. */
  color?: string;
  /** Static look for EVERY cell of the column (rules override per cell). */
  textColor?: string;
  background?: string;
  bold?: boolean;
  italic?: boolean;
  /** Conditional looks — first match wins. */
  rules?: TableCellRule[];
  /** Soft whole-cell highlight (tint + left accent bar). */
  highlight?: boolean;
  highlightColor?: string;
  /** Text decoration around the formatted value. */
  prefix?: string;
  suffix?: string;
  /** Native tooltip; `{value}` and `{<field>}` tokens are substituted from the row. */
  tooltip?: string;
  /** display 'link': href template (`{value}` / `{<field>}`); blank = the value. */
  linkTemplate?: string;
  /** Button-compatible route/query/state navigation, resolved against the clicked row. */
  navigateAction?: Record<string, unknown>;
  /** Dashboard navigation with the selected row's filter field. */
  navigation?: {
    targetDashboard: string;
    filterKey?: string;
    /** More columns of the clicked row sent along, e.g. customer name AND spec. */
    filterKeys?: string[];
    targetComponents?: string[];
    openInNewTab?: boolean;
  };
  /** Heat-map gradient ends (CSS colours). Default: pale → accent. */
  heatFrom?: string;
  heatTo?: string;
  /** Cell input (see TableCellEditor). Absent = the table type's default. */
  editor?: TableCellEditor;
  /** Dropdown values (editor 'dropdown' / a column control that lists options). */
  options?: string[];
  /** Also offer every distinct value the column currently holds. */
  optionsFromData?: boolean;
  /** Input placeholder. */
  placeholder?: string;
  /** display 'buttons': the buttons drawn in each cell (Approve / Reject / Request Info ...). */
  buttons?: TableCellButton[];
  [key: string]: unknown;
}

/** Colour of a cell button. */
export type TableCellButtonTone = 'green' | 'red' | 'blue' | 'amber' | 'grey';

/**
 * One button in a 'buttons' cell. `config` is a Button widget's feature settings
 * (dvm-action-button ButtonConfig: download / api / filter / navigate / popup incl.
 * a popup screen / approval / export); the host runs it with the clicked row as
 * context ({row} and each column of the row as tokens).
 */
export interface TableCellButton {
  id: string;
  label: string;
  tone?: TableCellButtonTone;
  /** outline = tinted border (default), filled = solid. */
  variant?: 'outline' | 'filled';
  config: Record<string, unknown>;
}

const TONES: TableCellButtonTone[] = ['green', 'red', 'blue', 'amber', 'grey'];

/** The valid buttons of a raw list (id + label required). */
export function normalizeTableCellButtons(raw: unknown): TableCellButton[] {
  return (Array.isArray(raw) ? raw : [])
    .filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    .map((item) => ({
      id: str(item['id']),
      label: str(item['label']),
      ...(TONES.includes(item['tone'] as TableCellButtonTone) ? { tone: item['tone'] as TableCellButtonTone } : {}),
      ...(item['variant'] === 'filled' ? { variant: 'filled' as const } : {}),
      config: item['config'] && typeof item['config'] === 'object' && !Array.isArray(item['config'])
        ? { ...(item['config'] as Record<string, unknown>) } : {},
    }))
    .filter((button) => !!button.id && !!button.label);
}

/** What a renderer needs for ONE cell after the config + value are resolved. */
export interface ResolvedCellLook {
  color?: string;
  background?: string;
  fontWeight?: string;
  fontStyle?: string;
  /** Rule / badge label (badge display). */
  label?: string;
  icon?: string;
  /** The matching rule (null = none matched; static look only). */
  rule: TableCellRule | null;
  /** The matching value-colour entry (absent / null = none). */
  valueColor?: TableCellValueColor | null;
  highlight: boolean;
}

/** Numeric column range (for data bars / heat maps). */
export interface CellValueRange { min: number; max: number }

const DISPLAYS: TableCellDisplay[] = ['text', 'badge', 'databar', 'progress', 'heatmap', 'trend', 'icon', 'link', 'buttons'];
const EDITORS: TableCellEditor[] = ['none', 'text', 'number', 'dropdown', 'checkbox', 'date', 'textarea'];
const OPS: TableCellRuleOp[] = ['>', '>=', '<', '<=', '=', '!=', 'between', 'contains', 'notContains', 'startsWith', 'endsWith', 'in', 'notIn', 'empty', 'notEmpty', 'any'];

/** Default accent when neither the config nor a rule gives one. */
export const TABLE_CELL_DEFAULT_ACCENT = '#3b82f6';

// ── Normalisation ────────────────────────────────────────────────────────────

const str = (v: unknown): string => (v === null || v === undefined ? '' : String(v)).trim();
const strOrUndef = (v: unknown): string | undefined => (str(v) ? str(v) : undefined);

/** Comma / newline separated text OR an array → trimmed, de-duplicated list. */
export function parseCellOptionList(raw: unknown): string[] {
  const parts = Array.isArray(raw) ? raw.map((v) => str(v)) : str(raw).split(/[\n,]/);
  const seen = new Set<string>();
  const out: string[] = [];
  parts.forEach((part) => {
    const value = part.trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  });
  return out;
}

/** Parse ONE rule from loose JSON (builder state / saved payload). Invalid
 *  operator → null (the rule is dropped). */
export function normalizeTableCellRule(raw: unknown): TableCellRule | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const op = str(r['op'] || r['operator']) as TableCellRuleOp;
  if (!OPS.includes(op)) return null;
  const rule: TableCellRule = { op };
  const value = r['value'];
  if (value !== undefined && value !== null && str(value) !== '') rule.value = typeof value === 'number' ? value : str(value);
  const value2 = r['value2'];
  if (value2 !== undefined && value2 !== null && str(value2) !== '') rule.value2 = typeof value2 === 'number' ? value2 : str(value2);
  const field = strOrUndef(r['field']);
  if (field) rule.field = field;
  const color = strOrUndef(r['color']);
  if (color) rule.color = color;
  const background = strOrUndef(r['background']);
  if (background) rule.background = background;
  if (r['bold'] === true) rule.bold = true;
  if (r['italic'] === true) rule.italic = true;
  const label = strOrUndef(r['label']);
  if (label) rule.label = label;
  const icon = strOrUndef(r['icon']);
  if (icon) rule.icon = icon;
  return rule;
}

/** Parse a LIST of rules from loose JSON (e.g. a KPI card's `valueRules`);
 *  anything that is not an array, and every invalid entry, is dropped. */
export function normalizeTableCellRules(raw: unknown): TableCellRule[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeTableCellRule).filter((rule): rule is TableCellRule => rule !== null);
}

/** Parse a value → colour map from loose JSON. Accepts the saved list form
 *  `[{ value, color, textColor?, label? }]` and the hand-written object form
 *  `{ "MFN": "#172033", "Other OEM": { "color": "#e0a21a", "textColor": "#172033" } }`.
 *  Entries without a value or a colour, and repeated values, are dropped. */
export function normalizeTableCellValueColors(raw: unknown): TableCellValueColor[] {
  if (!raw || typeof raw !== 'object') return [];
  const items: Array<Record<string, unknown>> = Array.isArray(raw)
    ? raw.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object')
    : Object.entries(raw as Record<string, unknown>).map(([value, look]) =>
      look && typeof look === 'object' ? { value, ...(look as Record<string, unknown>) } : { value, color: look });
  const seen = new Set<string>();
  const out: TableCellValueColor[] = [];
  items.forEach((r) => {
    const value = str(r['value']);
    const color = strOrUndef(r['color']);
    if (!value || !color) return;
    const key = value.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const entry: TableCellValueColor = { value, color };
    const textColor = strOrUndef(r['textColor']);
    if (textColor) entry.textColor = textColor;
    const label = strOrUndef(r['label']);
    if (label) entry.label = label;
    out.push(entry);
  });
  return out;
}

/** Parse a config from loose JSON. Returns `undefined` when nothing in it
 *  would change a cell, so callers can leave the key out of payloads. */
export function normalizeTableCellConfig(raw: unknown): TableCellConfig | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const r = raw as Record<string, unknown>;
  const cfg: TableCellConfig = {};
  const display = str(r['display']) as TableCellDisplay;
  if (display && display !== 'text' && DISPLAYS.includes(display)) cfg.display = display;
  const badgeFill = str(r['badgeFill']);
  if (badgeFill === 'solid' || badgeFill === 'soft') cfg.badgeFill = badgeFill;
  const valueColors = normalizeTableCellValueColors(r['valueColors']);
  if (valueColors.length) cfg.valueColors = valueColors;
  const color = strOrUndef(r['color']);
  if (color) cfg.color = color;
  const textColor = strOrUndef(r['textColor']);
  if (textColor) cfg.textColor = textColor;
  const background = strOrUndef(r['background']);
  if (background) cfg.background = background;
  if (r['bold'] === true) cfg.bold = true;
  if (r['italic'] === true) cfg.italic = true;
  const rules = (Array.isArray(r['rules']) ? r['rules'] : []).map(normalizeTableCellRule).filter((x): x is TableCellRule => !!x);
  if (rules.length) cfg.rules = rules;
  if (r['highlight'] === true) cfg.highlight = true;
  const highlightColor = strOrUndef(r['highlightColor']);
  if (highlightColor) cfg.highlightColor = highlightColor;
  const prefix = str(r['prefix']);
  if (prefix) cfg.prefix = prefix;
  const suffix = str(r['suffix']);
  if (suffix) cfg.suffix = suffix;
  const tooltip = strOrUndef(r['tooltip']);
  if (tooltip) cfg.tooltip = tooltip;
  const linkTemplate = strOrUndef(r['linkTemplate']);
  if (linkTemplate) cfg.linkTemplate = linkTemplate;
  const action = r['navigateAction'];
  if (action && typeof action === 'object' && !Array.isArray(action)) cfg.navigateAction = { ...(action as Record<string, unknown>) };
  const nav = r['navigation'] as Record<string, unknown> | undefined;
  if (nav && typeof nav === 'object') {
    cfg.navigation = {
      targetDashboard: str(nav['targetDashboard']),
      filterKey: strOrUndef(nav['filterKey']),
      ...(parseCellOptionList(nav['filterKeys']).length ? { filterKeys: parseCellOptionList(nav['filterKeys']) } : {}),
      targetComponents: parseCellOptionList(nav['targetComponents']),
      openInNewTab: nav['openInNewTab'] === true,
    };
  }
  const heatFrom = strOrUndef(r['heatFrom']);
  if (heatFrom) cfg.heatFrom = heatFrom;
  const heatTo = strOrUndef(r['heatTo']);
  if (heatTo) cfg.heatTo = heatTo;
  const editor = str(r['editor']) as TableCellEditor;
  if (editor && EDITORS.includes(editor)) cfg.editor = editor;
  const options = parseCellOptionList(r['options']);
  if (options.length) cfg.options = options;
  if (r['optionsFromData'] === true) cfg.optionsFromData = true;
  const placeholder = strOrUndef(r['placeholder']);
  if (placeholder) cfg.placeholder = placeholder;
  const buttons = normalizeTableCellButtons(r['buttons']);
  if (buttons.length) cfg.buttons = buttons;
  return isEmptyTableCellConfig(cfg) ? undefined : cfg;
}

/** True when the config carries nothing a renderer would act on. */
export function isEmptyTableCellConfig(cfg: TableCellConfig | null | undefined): boolean {
  if (!cfg) return true;
  return !(
    (cfg.display && cfg.display !== 'text')
    || cfg.badgeFill
    || (cfg.valueColors && cfg.valueColors.length)
    || cfg.color || cfg.textColor || cfg.background || cfg.bold || cfg.italic
    || (cfg.rules && cfg.rules.length)
    || cfg.highlight || cfg.prefix || cfg.suffix || cfg.tooltip || cfg.linkTemplate || cfg.navigateAction || cfg.navigation
    || (cfg.editor && cfg.editor !== 'none')
    || (cfg.options && cfg.options.length) || cfg.optionsFromData || cfg.placeholder
    || (cfg.buttons && cfg.buttons.length)
  );
}

// ── Value helpers ────────────────────────────────────────────────────────────

/** Numeric coercion tolerant of "1,234" / "$1.2k" → numeric part; else null. */
/** A whole-string formatted number: optional sign and currency, thousands
 *  commas, decimals / exponent, and a trailing %, k, M or B. */
const NUMERIC_TEXT = /^([+-]?)\s*[$€£¥₹]?\s*([+-]?)((?:\d+(?:,\d{3})*(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)\s*[%kmb]?$/i;

/**
 * Strict text → number: "1,234" → 1234, "$1.2k" → 1.2 (numeric part),
 * "-12.5%" → -12.5. Text that merely CONTAINS digits or the letter e is NOT a
 * number — stripping it to its digit-like characters turned "CNCMC" into ''
 * (→ 0) and "11QE-00010 HHSCTIL" into 1.1e-9, so code/name columns were typed
 * numeric and rendered as 0.
 */
export function parseNumericText(value: string): number | null {
  const m = NUMERIC_TEXT.exec(value.trim());
  if (!m || (m[1] && m[2])) return null;
  const n = Number(m[3].replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  return (m[1] || m[2]) === '-' ? -n : n;
}

export function cellToNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (typeof value === 'string') {
    // Digit-free text ("MFN", "Pending") is NOT a number: stripping it to ''
    // would coerce to 0 and make every such word equal under '=' rules.
    if (!/\d/.test(value)) return null;
    const n = Number(value.replace(/[^0-9.eE+-]/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

const isBlank = (v: unknown): boolean => v === null || v === undefined || str(v) === '';
const lower = (v: unknown): string => str(v).toLowerCase();

/** Whether ONE rule's condition holds for a value. */
export function cellRuleMatches(rule: TableCellRule, value: unknown): boolean {
  const op = rule.op;
  if (op === 'any') return true;
  if (op === 'empty') return isBlank(value);
  if (op === 'notEmpty') return !isBlank(value);
  if (op === 'contains' || op === 'notContains' || op === 'startsWith' || op === 'endsWith') {
    const hay = lower(value);
    const needle = lower(rule.value);
    if (!needle) return false;
    const hit = op === 'startsWith' ? hay.startsWith(needle) : op === 'endsWith' ? hay.endsWith(needle) : hay.includes(needle);
    return op === 'notContains' ? !hit : hit;
  }
  if (op === 'in' || op === 'notIn') {
    const list = parseCellOptionList(rule.value).map((v) => v.toLowerCase());
    const hit = list.includes(lower(value));
    return op === 'notIn' ? !hit : hit;
  }
  // Numeric / equality operators: compare as numbers when BOTH sides are
  // numeric, else (= / != only) as case-insensitive text.
  const n = cellToNumber(value);
  const v = cellToNumber(rule.value);
  if (op === '=' || op === '!=') {
    const equal = n !== null && v !== null ? n === v : lower(value) === lower(rule.value);
    return op === '=' ? equal : !equal;
  }
  if (n === null || v === null) return false;
  switch (op) {
    case '>': return n > v;
    case '>=': return n >= v;
    case '<': return n < v;
    case '<=': return n <= v;
    case 'between': { const v2 = cellToNumber(rule.value2); return v2 !== null && n >= Math.min(v, v2) && n <= Math.max(v, v2); }
    default: return false;
  }
}

/** First matching rule for a cell (rules may test another column via
 *  `rule.field`, read from `row`). */
export function matchCellRule(cfg: TableCellConfig | null | undefined, value: unknown, row?: Record<string, unknown> | null): TableCellRule | null {
  const rules = cfg?.rules;
  if (!Array.isArray(rules) || !rules.length) return null;
  for (const rule of rules) {
    const probe = rule.field && row ? readRowField(row, rule.field) : value;
    if (cellRuleMatches(rule, probe)) return rule;
  }
  return null;
}

/** Read a row field tolerantly: exact key, then a case/punctuation-insensitive
 *  match (backends may key rows by display name), then `measures.<key>`. */
export function readRowField(row: Record<string, unknown>, field: string): unknown {
  if (!row || !field) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, field)) return row[field];
  const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const want = norm(field);
  for (const key of Object.keys(row)) if (norm(key) === want) return row[key];
  const measures = row['measures'];
  if (measures && typeof measures === 'object') return (measures as Record<string, unknown>)[field];
  return undefined;
}

/** The value-colour entry a cell value maps to (null = none). Text compares
 *  trimmed and case-insensitively; numbers also by numeric value (10 = "10.0"). */
export function matchCellValueColor(cfg: TableCellConfig | null | undefined, value: unknown): TableCellValueColor | null {
  const list = cfg?.valueColors;
  if (!Array.isArray(list) || !list.length) return null;
  const text = lower(value);
  if (!text) return null;
  const n = cellToNumber(value);
  for (const entry of list) {
    if (lower(entry.value) === text) return entry;
    if (n !== null && cellToNumber(entry.value) === n) return entry;
  }
  return null;
}

/** Resolve the complete look of ONE cell: static config, then the value
 *  colour, then the first matching rule on top. Pure — call per cell at
 *  render / model-build time. */
export function resolveCellLook(cfg: TableCellConfig | null | undefined, value: unknown, row?: Record<string, unknown> | null): ResolvedCellLook {
  const look: ResolvedCellLook = { rule: null, highlight: false };
  if (!cfg) return look;
  if (cfg.textColor) look.color = cfg.textColor;
  if (cfg.background) look.background = cfg.background;
  if (cfg.bold) look.fontWeight = '600';
  if (cfg.italic) look.fontStyle = 'italic';
  if (cfg.highlight) {
    look.highlight = true;
    if (!look.background) look.background = cfg.highlightColor || '#fff7cc';
  }
  const valueColor = matchCellValueColor(cfg, value);
  if (valueColor) {
    look.valueColor = valueColor;
    // A badge takes the colour as its fill / tint (cellBadgeLookStyle); every
    // other display colours the text (the dot / bar reads it via cellAccent).
    if (cfg.display !== 'badge') look.color = valueColor.textColor || valueColor.color;
    if (valueColor.label) look.label = valueColor.label;
  }
  const rule = matchCellRule(cfg, value, row);
  if (rule) {
    look.rule = rule;
    if (rule.color) look.color = rule.color;
    if (rule.background) look.background = rule.background;
    if (rule.bold) look.fontWeight = '600';
    if (rule.italic) look.fontStyle = 'italic';
    if (rule.label) look.label = rule.label;
    if (rule.icon) look.icon = rule.icon;
  }
  return look;
}

/** `[ngStyle]`-ready map of the look's inline styles (empty when none). */
export function cellLookStyle(look: ResolvedCellLook | null | undefined): Record<string, string> {
  const style: Record<string, string> = {};
  if (!look) return style;
  if (look.color) style['color'] = look.color;
  if (look.background) style['background'] = look.background;
  if (look.fontWeight) style['font-weight'] = look.fontWeight;
  if (look.fontStyle) style['font-style'] = look.fontStyle;
  return style;
}

/** Accent colour for bars / dots / badges: the matching rule's colour, else
 *  the matching value colour, else the config accent, else the shared default. */
export function cellAccent(cfg: TableCellConfig | null | undefined, look?: ResolvedCellLook | null): string {
  return look?.rule?.color || look?.rule?.background || look?.valueColor?.color || cfg?.color || TABLE_CELL_DEFAULT_ACCENT;
}

/** White or dark text, whichever reads better over `color`. */
export function cellContrastText(color: string | undefined): string {
  const rgb = parseCssColor(color);
  if (!rgb) return '#ffffff';
  const luminance = (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
  return luminance > 0.6 ? '#172033' : '#ffffff';
}

/** The badge fill in effect: the configured one, else 'solid' once value
 *  colours exist (each value its own coloured chip), else 'soft'. */
export function cellBadgeFill(cfg: TableCellConfig | null | undefined): TableCellBadgeFill {
  return cfg?.badgeFill ?? (cfg?.valueColors?.length ? 'solid' : 'soft');
}

/** `[ngStyle]` of a 'badge' display cell for a resolved look — the ONE badge
 *  painter every table type uses. Colour precedence: the matching rule, then
 *  the matching value colour, then the config accent. A solid fill paints
 *  the colour as the fill under contrasting (or the entry's) text; a soft
 *  fill shows the colour as text over a translucent wash (see cellBadgeFill). */
export function cellBadgeLookStyle(cfg: TableCellConfig | null | undefined, look?: ResolvedCellLook | null): Record<string, string> {
  const rule = look?.rule ?? null;
  const valueColor = look?.valueColor ?? null;
  let style: Record<string, string>;
  if (cellBadgeFill(cfg) === 'solid') {
    const fill = rule?.background || rule?.color || valueColor?.color || cfg?.color || TABLE_CELL_DEFAULT_ACCENT;
    const text = (rule?.background && rule.color) || (!rule && valueColor?.textColor) || cellContrastText(fill);
    style = { background: fill, color: text };
  } else {
    style = cellBadgeStyle(cellAccent(cfg, look));
    if (rule?.background) {
      style['background'] = rule.background;
      style['color'] = rule.color || cellContrastText(rule.background);
    } else if (rule?.color) {
      style['color'] = rule.color;
    } else if (valueColor?.textColor) {
      style['color'] = valueColor.textColor;
    }
  }
  if (look?.fontStyle) style['font-style'] = look.fontStyle;
  return style;
}

/** Wrap the formatted text with the config's prefix / suffix. */
export function decorateCellText(cfg: TableCellConfig | null | undefined, text: string): string {
  if (!cfg || text === '' || text === null || text === undefined) return text ?? '';
  return `${cfg.prefix ?? ''}${text}${cfg.suffix ?? ''}`;
}

/** Substitute `{value}` and `{<field>}` tokens from the row (missing → ''). */
export function substituteCellTokens(template: string | undefined, value: unknown, row?: Record<string, unknown> | null, encodeValues = false): string {
  if (!template) return '';
  return template.replace(/\{([^{}]+)\}/g, (_m, token: string) => {
    const key = token.trim();
    const v = key === 'value' ? value : row ? readRowField(row, key) : undefined;
    const text = isBlank(v) ? '' : str(v);
    return encodeValues ? encodeURIComponent(text) : text;
  });
}

/** Whether a display kind needs the column's numeric range. */
export function cellDisplayNeedsRange(cfg: TableCellConfig | null | undefined): boolean {
  return cfg?.display === 'databar' || cfg?.display === 'heatmap';
}

/** Numeric range of a column across rows (null when no numeric value). */
export function cellValueRange(values: unknown[]): CellValueRange | null {
  let min = Infinity;
  let max = -Infinity;
  values.forEach((v) => {
    const n = cellToNumber(v);
    if (n === null) return;
    if (n < min) min = n;
    if (n > max) max = n;
  });
  return min === Infinity ? null : { min, max };
}

/** 0..100 width of a data bar / progress fill. Progress reads the value as
 *  a percentage directly (0..100, or 0..1 when every value is ≤ 1). */
export function cellBarPercent(cfg: TableCellConfig | null | undefined, value: unknown, range?: CellValueRange | null): number {
  const n = cellToNumber(value);
  if (n === null) return 0;
  if (cfg?.display === 'progress') {
    const pct = range && range.max <= 1 && range.min >= 0 ? n * 100 : n;
    return Math.max(0, Math.min(100, pct));
  }
  if (!range || range.max === range.min) return n > 0 ? 100 : 0;
  // Bars grow from the column minimum (or 0 when the column crosses it).
  const base = range.min > 0 ? 0 : range.min;
  return Math.max(0, Math.min(100, ((n - base) / (range.max - base || 1)) * 100));
}

/** Heat-map background scaled from `heatFrom` to `heatTo` across the range. */
export function cellHeatColor(cfg: TableCellConfig | null | undefined, value: unknown, range?: CellValueRange | null): string {
  const n = cellToNumber(value);
  if (n === null || !range) return '';
  const t = range.max === range.min ? 1 : Math.max(0, Math.min(1, (n - range.min) / (range.max - range.min)));
  const from = parseCssColor(cfg?.heatFrom || '#eff6ff') || [239, 246, 255];
  const to = parseCssColor(cfg?.heatTo || cfg?.color || TABLE_CELL_DEFAULT_ACCENT) || [37, 99, 235];
  const mix = (i: number): number => Math.round(from[i] + (to[i] - from[i]) * t);
  return `rgba(${mix(0)}, ${mix(1)}, ${mix(2)}, 0.85)`;
}

/** Trend glyph + colour for a numeric value (▲ green / ▼ red / – grey). */
export function cellTrend(value: unknown): { icon: string; color: string; dir: 'up' | 'down' | 'flat' } {
  const n = cellToNumber(value);
  if (n === null || n === 0) return { icon: '–', color: '#64748b', dir: 'flat' };
  return n > 0 ? { icon: '▲', color: '#16a34a', dir: 'up' } : { icon: '▼', color: '#dc2626', dir: 'down' };
}

/** Badge tint derived from a colour: the colour as text on a translucent
 *  wash of itself — readable on any background without per-colour CSS. */
export function cellBadgeStyle(color: string): Record<string, string> {
  const rgb = parseCssColor(color);
  if (!rgb) return { color, background: 'rgba(100, 116, 139, 0.14)' };
  return { color, background: `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, 0.14)` };
}

/** The unified table `control` a cell editor maps to (undefined = none). */
export function cellEditorControl(cfg: TableCellConfig | null | undefined): 'text' | 'number' | 'dropdown' | 'checkbox' | 'date' | 'textarea' | undefined {
  const e = cfg?.editor;
  if (!e || e === 'none') return undefined;
  return e;
}

/** Dropdown options for a column: the configured list, plus (when
 *  `optionsFromData`) the distinct values the rows hold — in that order. */
export function cellDropdownOptions(cfg: TableCellConfig | null | undefined, dataValues: unknown[] = []): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (v: unknown): void => { const t = str(v); if (t && !seen.has(t)) { seen.add(t); out.push(t); } };
  (cfg?.options || []).forEach(push);
  if (cfg?.optionsFromData) {
    const sorted = Array.from(new Set(dataValues.map(str).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    sorted.forEach(push);
  }
  return out;
}

/** "#rrggbb" / "#rgb" / "rgb(a)(…)" → [r, g, b]; anything else → null. */
export function parseCssColor(input: string | undefined): [number, number, number] | null {
  const s = str(input);
  if (!s) return null;
  const hex = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((c) => c + c).join('') : hex[1];
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }
  const rgb = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
}
